'use client';

import type { OnboardingInviteRecipient } from 'src/features/advisor-book-onboarding/api/onboarding-cockpit-gql';
import { useState } from 'react';
import { Checkbox } from '@savvy-kit/components/checkbox';
import { cn } from '@savvy-kit/lib/utils';

type ResendInviteRecipientPickerProps = {
  readonly recipients: readonly OnboardingInviteRecipient[];
  /** Seeded with every eligible recipient; mutated in place so the dialog's confirm handler reads the live selection. */
  readonly selection: Set<string>;
};

const ROLE_LABELS: Record<string, string> = {
  primary: 'Primary',
  joint_owner: 'Joint owner',
};

function ineligibleReason(recipient: OnboardingInviteRecipient) {
  return recipient.submitted ? 'Already submitted — nothing to resend' : 'No email on file';
}

export function ResendInviteRecipientPicker({ recipients, selection }: ResendInviteRecipientPickerProps) {
  const [selectedIds, setSelectedIds] = useState<readonly string[]>(() => [...selection]);

  function toggle(id: string, checked: boolean) {
    if (checked) {
      selection.add(id);
    } else {
      selection.delete(id);
    }
    setSelectedIds([...selection]);
  }

  return (
    <div className="flex flex-col gap-4 text-left">
      <p className="text-sm text-secondary">
        Choose who to resend to. Each person gets their own link, and resending replaces the link they already have.
      </p>

      <ul className="flex flex-col gap-2">
        {recipients.map((recipient) => {
          const name = recipient.name ?? recipient.email ?? 'Client';
          const role = ROLE_LABELS[recipient.role] ?? recipient.role;

          return (
            <li
              className={cn('rounded-md border border-border p-3', !recipient.canResend && 'opacity-60')}
              key={recipient.id}
            >
              <Checkbox
                checked={selectedIds.includes(recipient.id)}
                description={recipient.canResend ? (recipient.email ?? undefined) : ineligibleReason(recipient)}
                disabled={!recipient.canResend}
                id={`resend-recipient-${recipient.id}`}
                label={
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-dark">{name}</span>
                    <span className="text-xs text-secondary">{role}</span>
                  </span>
                }
                onCheckedChange={(next) => toggle(recipient.id, next === true)}
              />
            </li>
          );
        })}
      </ul>

      {selectedIds.length === 0 && <p className="text-sm text-destructive">Select at least one person to resend to.</p>}
    </div>
  );
}
